import { makeObservable, observable, action, runInAction, reaction, computed } from 'mobx';
import {
  fetchRecent,
  fetchTrace,
  fetchByThread,
  fetchStatus,
  fetchHypothesis,
  fetchTraceTelemetry,
  type ActivityEvent,
  type TurnHypothesis,
  type TraceTelemetry,
} from '@/api/activity';
import type { RootStore } from './RootStore';

// Live runtime feed of LLM calls + tool calls + A2A hops. Refetches when the
// user changes merchant / time window / filters. Drawer state for trace +
// thread deep-dives.

export class ActivityStore {
  @observable events: ActivityEvent[] = [];
  @observable isLoading = false;
  @observable error: string | null = null;

  // Window expressed as either a "preset" (in hours, computed relative to
  // now) OR a custom absolute range. customFrom/customTo win when set.
  // Mirrors the App Insights time-range model.
  @observable presetHours: number = 24;
  @observable customFrom: string | null = null; // ISO string or null
  @observable customTo: string | null = null;   // ISO string or null

  @observable agentFilter: number | null = null;
  @observable kindFilter: 'all' | 'llm' | 'tool' | 'consult' | 'retrieval' = 'all';
  @observable onlyIssues = false;
  @observable search = '';
  @observable autoRefresh = false;

  // Drawer state
  @observable openCorrelationId: string | null = null;
  @observable openThreadId: string | null = null;
  @observable drawerEvents: ActivityEvent[] = [];
  @observable isLoadingDrawer = false;
  @observable drawerError: string | null = null;

  // AI Hypothesis state — populated on drawer open. The backend caches
  // the result for 5 minutes per correlation id, so re-opens are cheap.
  @observable hypothesis: TurnHypothesis | null = null;
  @observable isLoadingHypothesis = false;
  @observable hypothesisError: string | null = null;

  // App Insights spans for the open turn (exceptions + dependencies).
  // Loaded in parallel with the SQL trace; stays empty when App Insights
  // isn't configured or the correlationId has no matching spans yet.
  @observable traceTelemetry: TraceTelemetry | null = null;
  @observable isLoadingTraceTelemetry = false;
  @observable traceTelemetryError: string | null = null;

  // App Insights configured flag (drives the "configure to unlock" banner)
  @observable appInsightsConfigured: boolean | null = null;

  private timer: number | null = null;
  private drawerEpoch = 0;
  private feedEpoch = 0;

  constructor(private root: RootStore) {
    makeObservable(this);

    // App Insights configured-flag is global (not merchant-scoped) — fetch
    // it once at construction so the banner state is correct even before
    // the user picks a merchant. The Status endpoint is auth-only, no
    // merchant required.
    void this.fetchStatus();

    reaction(
      () => this.root.merchantContextStore.selectedMerchantId,
      (merchantId) => {
        this.closeDrawer();
        this.feedEpoch++;
        this.events = [];
        if (merchantId != null) {
          void this.fetch();
        } else {
          // Clear stale results AND any prior error so the page doesn't
          // keep showing "Failed to load activity" from a previous merchant
          // session after the user navigates back to the global picker.
          runInAction(() => {
            this.events = [];
            this.error = null;
            this.isLoading = false;
          });
        }
      },
      { fireImmediately: true }
    );

    // Auto-refresh polling: every 10s when on
    reaction(
      () => ({ on: this.autoRefresh, hours: this.presetHours, agent: this.agentFilter, issues: this.onlyIssues }),
      ({ on }) => {
        if (this.timer != null) {
          window.clearInterval(this.timer);
          this.timer = null;
        }
        if (on) {
          this.timer = window.setInterval(() => void this.fetch(), 10_000);
        }
      }
    );
  }

  @action setPreset(hours: number): void {
    this.presetHours = hours;
    this.customFrom = null;
    this.customTo = null;
    void this.fetch();
  }
  @action setCustomRange(fromIso: string, toIso: string): void {
    this.customFrom = fromIso;
    this.customTo = toIso;
    void this.fetch();
  }
  @action clearCustomRange(): void {
    this.customFrom = null;
    this.customTo = null;
    void this.fetch();
  }
  @computed get isCustomRange(): boolean {
    return this.customFrom != null && this.customTo != null;
  }
  @action setAgentFilter(v: number | null): void {
    this.agentFilter = v;
    void this.fetch();
  }
  @action setKindFilter(v: 'all' | 'llm' | 'tool' | 'consult' | 'retrieval'): void {
    this.kindFilter = v;
  }
  @action setOnlyIssues(v: boolean): void {
    this.onlyIssues = v;
    void this.fetch();
  }
  @action setSearch(v: string): void {
    this.search = v;
  }
  @action setAutoRefresh(v: boolean): void {
    this.autoRefresh = v;
  }

  @action
  async fetchStatus(): Promise<void> {
    try {
      const s = await fetchStatus();
      runInAction(() => {
        this.appInsightsConfigured = s.appInsightsConfigured;
      });
    } catch {
      runInAction(() => {
        this.appInsightsConfigured = false;
      });
    }
  }

  @action
  async fetch(): Promise<void> {
    if (this.root.merchantContextStore.selectedMerchantId == null) return;
    const epoch = ++this.feedEpoch;
    this.isLoading = true;
    this.error = null;
    try {
      const items = await fetchRecent({
        hours: this.customFrom ? undefined : this.presetHours,
        fromUtc: this.customFrom ?? undefined,
        toUtc: this.customTo ?? undefined,
        agentId: this.agentFilter ?? undefined,
        onlyIssues: this.onlyIssues || undefined,
        pageSize: 300,
      });
      runInAction(() => {
        if (epoch !== this.feedEpoch) return;
        this.events = items;
        this.isLoading = false;
      });
    } catch (err) {
      runInAction(() => {
        if (epoch !== this.feedEpoch) return;
        this.isLoading = false;
        this.error = err instanceof Error ? err.message : 'Failed to load activity';
      });
    }
  }

  @computed get filtered(): ActivityEvent[] {
    const term = this.search.trim().toLowerCase();
    return this.events.filter((e) => {
      if (this.kindFilter !== 'all' && e.kind !== this.kindFilter) return false;
      if (term) {
        const blob = `${e.agentName ?? ''} ${e.modelName ?? ''} ${e.pluginName ?? ''} ${e.methodName ?? ''} ${e.peerAgentName ?? ''} ${e.threadId ?? ''} ${e.correlationId ?? ''}`.toLowerCase();
        if (!blob.includes(term)) return false;
      }
      return true;
    });
  }

  @computed get summary(): {
    total: number;
    issues: number;
    errored: number;
    llm: number;
    tool: number;
    consult: number;
    retrieval: number;
  } {
    let issues = 0;
    let errored = 0;
    let llm = 0;
    let tool = 0;
    let consult = 0;
    let retrieval = 0;
    for (const e of this.filtered) {
      if (e.issueTags.length > 0) issues++;
      if (e.errored) errored++;
      if (e.kind === 'llm') llm++;
      else if (e.kind === 'tool') tool++;
      else if (e.kind === 'consult') consult++;
      else if (e.kind === 'retrieval') retrieval++;
    }
    return { total: this.filtered.length, issues, errored, llm, tool, consult, retrieval };
  }

  // ── Drawer / trace / thread ─────────────────────────────────────────────

  @action
  async openTrace(correlationId: string): Promise<void> {
    const epoch = ++this.drawerEpoch;
    this.openCorrelationId = correlationId;
    this.openThreadId = null;
    this.isLoadingDrawer = true;
    this.drawerError = null;
    this.drawerEvents = [];
    this.hypothesis = null;
    this.hypothesisError = null;
    this.isLoadingHypothesis = false;
    this.traceTelemetry = null;
    this.traceTelemetryError = null;
    this.isLoadingTraceTelemetry = false;
    try {
      const items = await fetchTrace(correlationId);
      runInAction(() => {
        if (epoch !== this.drawerEpoch) return;
        this.drawerEvents = items;
        this.isLoadingDrawer = false;
      });
    } catch (err) {
      runInAction(() => {
        if (epoch !== this.drawerEpoch) return;
        this.isLoadingDrawer = false;
        this.drawerError = err instanceof Error ? err.message : 'Failed to load trace';
      });
    }
    // App Insights spans fetched after the trace data — cheap,
    // no LLM cost. Hypothesis is opt-in: it burns tokens, so we only run
    // it when the engineer clicks "Generate AI hypothesis" in the card.
    if (epoch === this.drawerEpoch) void this.loadTraceTelemetry(correlationId);
  }

  @action
  async openThread(threadId: string): Promise<void> {
    const epoch = ++this.drawerEpoch;
    this.openThreadId = threadId;
    this.openCorrelationId = null;
    this.isLoadingDrawer = true;
    this.drawerError = null;
    this.drawerEvents = [];
    this.hypothesis = null;
    this.hypothesisError = null;
    this.isLoadingHypothesis = false;
    this.traceTelemetry = null;
    this.traceTelemetryError = null;
    this.isLoadingTraceTelemetry = false;
    try {
      const items = await fetchByThread(threadId);
      runInAction(() => {
        if (epoch !== this.drawerEpoch) return;
        this.drawerEvents = items;
        this.isLoadingDrawer = false;
      });
    } catch (err) {
      runInAction(() => {
        if (epoch !== this.drawerEpoch) return;
        this.isLoadingDrawer = false;
        this.drawerError = err instanceof Error ? err.message : 'Failed to load thread';
      });
    }
    // Hypothesis is opt-in (token cost) — see openTrace comment. Engineer
    // clicks "Generate AI hypothesis" in the card when they want it.
    // Thread view also skips App Insights — one thread spans many turns
    // and we don't have a single correlation id to query against.
  }

  @action
  async loadTraceTelemetry(correlationId: string): Promise<void> {
    const epoch = this.drawerEpoch;
    this.isLoadingTraceTelemetry = true;
    this.traceTelemetryError = null;
    try {
      const t = await fetchTraceTelemetry(correlationId);
      runInAction(() => {
        if (epoch !== this.drawerEpoch) return;
        this.traceTelemetry = t;
        this.isLoadingTraceTelemetry = false;
      });
    } catch (err) {
      runInAction(() => {
        if (epoch !== this.drawerEpoch) return;
        this.isLoadingTraceTelemetry = false;
        this.traceTelemetryError = err instanceof Error ? err.message : 'Failed to load App Insights data';
      });
    }
  }

  @action
  async loadHypothesis(params: { correlationId?: string; threadId?: string }): Promise<void> {
    const epoch = this.drawerEpoch;
    this.isLoadingHypothesis = true;
    this.hypothesisError = null;
    try {
      const h = await fetchHypothesis(params);
      runInAction(() => {
        if (epoch !== this.drawerEpoch) return;
        this.hypothesis = h;
        this.isLoadingHypothesis = false;
      });
    } catch (err) {
      runInAction(() => {
        if (epoch !== this.drawerEpoch) return;
        this.isLoadingHypothesis = false;
        this.hypothesisError = err instanceof Error ? err.message : 'Failed to analyze turn';
      });
    }
  }

  /**
   * Run hypothesis analysis for whichever trace context is currently open.
   * Called from the "Generate AI hypothesis" button so the user opts into
   * the LLM call rather than having it fire automatically when the drawer
   * opens (which silently burns tokens — see ActivityStore.openTrace).
   */
  @action
  async generateHypothesisForOpen(): Promise<void> {
    if (this.openCorrelationId) {
      await this.loadHypothesis({ correlationId: this.openCorrelationId });
    } else if (this.openThreadId) {
      await this.loadHypothesis({ threadId: this.openThreadId });
    }
  }

  @action
  closeDrawer(): void {
    this.drawerEpoch++;
    this.isLoadingDrawer = false;
    this.isLoadingTraceTelemetry = false;
    this.isLoadingHypothesis = false;
    this.openCorrelationId = null;
    this.openThreadId = null;
    this.drawerEvents = [];
    this.drawerError = null;
    this.hypothesis = null;
    this.hypothesisError = null;
    this.traceTelemetry = null;
    this.traceTelemetryError = null;
  }
}
